import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { ErrorPanel } from '../components/ui/ErrorPanel';
import { LoadingRows } from '../components/ui/Skeleton';
import { useAuthOrSession } from '../lib/authOrSession';
import {
  getProfile,
  updateProfile,
  uploadSignature,
  type UserProfileRow,
} from '../lib/supabaseUsers';

interface FormState {
  displayName: string;
  salutation: string;
  title: string;
  providerName: string;
  providerAddressLine1: string;
  providerAddressLine2: string;
  providerPhoneEmail: string;
  certifications: string;
  signatureUrl: string;
}

const EMPTY: FormState = {
  displayName: '',
  salutation: '',
  title: '',
  providerName: '',
  providerAddressLine1: '',
  providerAddressLine2: '',
  providerPhoneEmail: '',
  certifications: '',
  signatureUrl: '',
};

const SALUTATION_OPTIONS = ['', 'Herr', 'Frau', 'Divers'] as const;

type SaveState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

/**
 * Berater-Profil-Form (AP-B3).
 *
 * Editorial-Stil konsistent mit NewCase / Cockpit: Stone-Palette, font-serif
 * Headlines, Sektionskarten mit border-stone-200. Sie-Form, behördentauglich.
 *
 * Sektionen:
 *  1. Person — Name, Anrede, Titel, E-Mail (read-only).
 *  2. Briefkopf — Provider-Name, Adresse, Telefon/E-Mail, Zertifizierungen.
 *  3. Signatur — PNG-Upload mit Live-Vorschau.
 *
 * Persistenz: getProfile() beim Mount, updateProfile() bei Submit.
 * Signatur-Upload überschreibt bestehende Datei (Cache-Buster im Filename).
 */
export function ProfileForm() {
  const navigate = useNavigate();
  const { user, isMock } = useAuthOrSession();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profil beim Mount laden (Production-Pfad).
  useEffect(() => {
    if (isMock) {
      setSave({ kind: 'idle' });
      return;
    }
    if (!user) return;
    setSave({ kind: 'loading' });
    void getProfile()
      .then((row) => {
        if (row) setForm(rowToForm(row));
        setSave({ kind: 'idle' });
      })
      .catch((err: unknown) => {
        setSave({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Profil konnte nicht geladen werden.',
        });
      });
  }, [isMock, user]);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isMock) {
      setSave({ kind: 'error', message: 'Im Dev-Mock-Modus wird das Profil nicht gespeichert.' });
      return;
    }
    setSave({ kind: 'saving' });
    try {
      await updateProfile({
        displayName: emptyToNull(form.displayName),
        salutation: emptyToNull(form.salutation),
        title: emptyToNull(form.title),
        providerName: emptyToNull(form.providerName),
        providerAddressLine1: emptyToNull(form.providerAddressLine1),
        providerAddressLine2: emptyToNull(form.providerAddressLine2),
        providerPhoneEmail: emptyToNull(form.providerPhoneEmail),
        certifications: emptyToNull(form.certifications),
        signatureUrl: emptyToNull(form.signatureUrl),
      });
      setSave({ kind: 'saved' });
    } catch (err) {
      setSave({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Speichern fehlgeschlagen.',
      });
    }
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    setUploadError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (isMock) {
      setUploadError('Im Dev-Mock-Modus ist kein Signatur-Upload möglich.');
      return;
    }
    setUploading(true);
    try {
      const result = await uploadSignature(file);
      patch('signatureUrl', result.publicUrl);
      // Signatur-URL sofort persistieren, damit ein Reload sie wiederfindet,
      // ohne dass der Berater zusätzlich „Speichern" klicken muss.
      await updateProfile({ signatureUrl: result.publicUrl });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleRemoveSignature() {
    if (isMock) return;
    setUploading(true);
    setUploadError(null);
    try {
      await updateProfile({ signatureUrl: null });
      patch('signatureUrl', '');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Entfernen fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="pb-24 pt-12 md:pt-20">
      <button
        type="button"
        onClick={() => {
          void navigate('/cases');
        }}
        className="mb-6 inline-flex items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft size={14} />
        Mandate
      </button>

      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Berater-Profil
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
          IHR PROFIL.
        </h1>
        <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:text-lg">
          Stamm- und Briefkopfdaten, die in jeder von Ihnen gezeichneten Stellungnahme erscheinen.
          Die Signatur wird unten in das DOCX eingebettet.
        </p>
      </header>

      {save.kind === 'loading' && <LoadingRows rows={2} label="Profil wird geladen" />}

      {isMock && (
        <section className="mb-8 rounded-sm border border-amber-200 bg-amber-50 px-6 py-4 md:px-10">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
            Dev-Modus · Mock-Auth
          </p>
          <p className="mt-1 font-serif text-sm italic text-amber-900">
            Im Dev-Mock werden Eingaben nicht persistiert. Im Production-Build wird das Profil über
            die Supabase-Tabelle `users` gespeichert.
          </p>
        </section>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="space-y-12"
      >
        {/* 1. Person */}
        <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
          <SectionHeader
            eyebrow="Person"
            title="STAMMDATEN"
            description="So werden Sie in der Stellungnahme als zeichnender Berater geführt."
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <SelectField
              label="Anrede"
              value={form.salutation}
              onChange={(v) => {
                patch('salutation', v);
              }}
              options={SALUTATION_OPTIONS.map((s) => ({
                value: s,
                label: s === '' ? '— wählen —' : s,
              }))}
            />
            <TextField
              label="Titel"
              hint="optional, z.B. Dr."
              value={form.title}
              onChange={(v) => {
                patch('title', v);
              }}
              placeholder="Dr."
            />
            <TextField
              label="Vollständiger Name"
              value={form.displayName}
              onChange={(v) => {
                patch('displayName', v);
              }}
              placeholder="Christian Kopsch"
            />
          </div>
          <ReadOnlyField label="E-Mail (aus Sitzung)" value={user?.email ?? '—'} />
        </section>

        {/* 2. Briefkopf */}
        <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
          <SectionHeader
            eyebrow="Briefkopf"
            title="ABSENDER-DATEN"
            description="Erscheinen oben links in jeder generierten Stellungnahme."
          />
          <TextField
            label="Provider-Name"
            value={form.providerName}
            onChange={(v) => {
              patch('providerName', v);
            }}
            placeholder="Petschwork Consulting GmbH"
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <TextField
              label="Adresse · Zeile 1"
              value={form.providerAddressLine1}
              onChange={(v) => {
                patch('providerAddressLine1', v);
              }}
              placeholder="Musterstr. 5"
            />
            <TextField
              label="Adresse · Zeile 2"
              value={form.providerAddressLine2}
              onChange={(v) => {
                patch('providerAddressLine2', v);
              }}
              placeholder="81543 München"
            />
          </div>
          <TextField
            label="Telefon · E-Mail"
            hint="einzeilig, beliebig formatiert"
            value={form.providerPhoneEmail}
            onChange={(v) => {
              patch('providerPhoneEmail', v);
            }}
            placeholder="Tel. 089 123456 · post@petschwork.de"
          />
          <TextField
            label="Zertifizierungs-Zeile"
            hint="Erscheint unter Ihrer Unterschrift als fachlicher Nachweis."
            value={form.certifications}
            onChange={(v) => {
              patch('certifications', v);
            }}
            placeholder="AVGS § 45 SGB III · BAFA-zertifiziert"
          />
        </section>

        {/* 3. Signatur */}
        <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
          <SectionHeader
            eyebrow="Signatur"
            title="UNTERSCHRIFT"
            description="PNG, JPEG oder SVG · maximal 2 MB · transparenter Hintergrund empfohlen."
          />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Vorschau */}
            <div className="rounded-sm border border-dashed border-stone-300 bg-stone-50 p-6">
              <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
                Vorschau
              </p>
              <div className="mt-3 flex min-h-[120px] items-center justify-center bg-white p-4">
                {form.signatureUrl ? (
                  <img
                    src={form.signatureUrl}
                    alt="Signatur-Vorschau"
                    className="max-h-24 max-w-full object-contain"
                  />
                ) : (
                  <p className="font-serif text-sm italic text-stone-500">
                    Noch keine Signatur hochgeladen.
                  </p>
                )}
              </div>
            </div>

            {/* Upload-Steuerung */}
            <div className="space-y-3">
              <label
                htmlFor="signature-upload"
                className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-stone-900 bg-white px-4 py-3 font-sans text-xs font-medium uppercase tracking-widest text-stone-900 hover:bg-stone-900 hover:text-white"
              >
                <Upload size={14} />
                {uploading ? 'Lade hoch…' : 'Datei wählen'}
              </label>
              <input
                ref={fileInputRef}
                id="signature-upload"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => {
                  void handleFile(e);
                }}
                className="sr-only"
                disabled={uploading || isMock}
              />
              {form.signatureUrl && (
                <button
                  type="button"
                  onClick={() => {
                    void handleRemoveSignature();
                  }}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-red-700 disabled:opacity-40"
                >
                  <X size={12} />
                  Signatur entfernen
                </button>
              )}
              {uploadError && <p className="font-sans text-xs text-red-700">{uploadError}</p>}
              <p className="font-serif text-xs italic text-stone-500">
                Die Signatur wird nach dem Upload sofort gespeichert. Das Profil-Formular speichert
                Sie zusätzlich bei „Profil speichern".
              </p>
            </div>
          </div>
        </section>

        {/* Submit */}
        <section className="flex flex-col items-start gap-4 border-t border-stone-200 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="font-serif text-sm italic text-stone-500">
            Sie können das Profil jederzeit ändern. Bestehende Stellungnahmen bleiben unverändert.
          </div>
          <div className="flex flex-col items-end gap-3">
            {save.kind === 'error' && (
              <div className="w-full max-w-md">
                <ErrorPanel messages={[save.message]} />
              </div>
            )}
            {save.kind === 'saved' && (
              <p className="font-sans text-xs font-medium uppercase tracking-widest text-emerald-700">
                ✓ Profil gespeichert
              </p>
            )}
            <button
              type="submit"
              disabled={save.kind === 'saving' || save.kind === 'loading'}
              className="inline-flex items-center gap-2 rounded-sm bg-stone-900 px-6 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-400"
            >
              {save.kind === 'saving' ? 'Speichere…' : 'Profil speichern →'}
            </button>
          </div>
        </section>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function rowToForm(r: UserProfileRow): FormState {
  return {
    displayName: r.display_name ?? '',
    salutation: r.salutation ?? '',
    title: r.title ?? '',
    providerName: r.provider_name ?? '',
    providerAddressLine1: r.provider_address_line1 ?? '',
    providerAddressLine2: r.provider_address_line2 ?? '',
    providerPhoneEmail: r.provider_phone_email ?? '',
    certifications: r.certifications ?? '',
    signatureUrl: r.signature_url ?? '',
  };
}

function emptyToNull(s: string): string | null {
  const t = s.trim();
  return t.length === 0 ? null : t;
}

// -----------------------------------------------------------------------------
// Form-Sub-Components — bewusst lokal gehalten, identisches Pattern wie NewCase
// -----------------------------------------------------------------------------

interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  description: string;
}

function SectionHeader({ eyebrow, title, description }: SectionHeaderProps) {
  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {eyebrow}
      </p>
      <h2 className="mt-1 font-serif text-xl font-bold uppercase tracking-tight text-stone-900">
        {title}
      </h2>
      <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">{description}</p>
    </div>
  );
}

interface TextFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

function TextField({ label, hint, value, onChange, placeholder }: TextFieldProps) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
      >
        {label}
      </label>
      {hint && <p className="mt-1 font-serif text-xs italic text-stone-500">{hint}</p>}
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}

function SelectField({ label, value, onChange, options }: SelectFieldProps) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface ReadOnlyFieldProps {
  label: string;
  value: string;
}

function ReadOnlyField({ label, value }: ReadOnlyFieldProps) {
  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700">
        {label}
      </p>
      <p className="mt-1 rounded-sm border border-stone-100 bg-stone-50 px-3 py-2 font-serif text-base text-stone-500">
        {value}
      </p>
    </div>
  );
}
